import React, { useState, useMemo, useEffect } from "react";
import {
  FaPlus,
  FaFilePdf,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaTruck,
} from "react-icons/fa";

// Colore per stato
const getColorByStato = (stato) => {
  switch (stato) {
    case "Da fare": return "red";
    case "In corso": return "orange";
    case "Completato": return "green";
    default: return "gray";
  }
};

// Normalizzazione per ricerca (case/accents-insensitive)
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const haystack = (i) =>
  normalize([
    i.cliente,
    i.indirizzo,
    i.causale,
    i.lavoriEseguiti ?? i.lavori_eseguiti,
    i.tecnico,
    i.telefono,
    i.email,
    i.numero_foglio_intervento ?? i.numeroFoglioIntervento,
    i.note,
  ]
    .filter(Boolean)
    .join(" "));

const TabellaInterventi = ({
  interventi,
  tipo, // "aperti" | "chiusi"
  onModifica,
  onCancella,
  onPDF,
  onAggiungi,
  onImpostaFlotta,
  flottaInterventoId,
  // opzionali: se non li passi, usa localQuery
  searchQuery,
  onSearchChange,
}) => {
  // Espansione campi lunghi
  const [expandedFields, setExpandedFields] = useState({});
  // Stato "fatturato" (solo visual)
  const [fatturati, setFatturati] = useState({});
  // Query locale (fallback se non arriva dal parent)
  const [localQuery, setLocalQuery] = useState("");

  // Se il parent fornisce searchQuery, tieni allineata la locale
  useEffect(() => {
    if (typeof searchQuery === "string") setLocalQuery(searchQuery);
  }, [searchQuery]);

  const effectiveQuery = typeof searchQuery === "string" ? searchQuery : localQuery;

  const toggleExpand = (interventoId, field) => {
    setExpandedFields((prev) => ({
      ...prev,
      [`${interventoId}-${field}`]: !prev[`${interventoId}-${field}`],
    }));
  };

  // Filtro con useMemo
  const q = normalize(effectiveQuery || "");
  const filteredInterventi = useMemo(() => {
    if (!q) return interventi;
    return interventi.filter((i) => haystack(i).includes(q));
  }, [interventi, q]);

  // Raggruppo per data (robusto)
  const interventiPerData = useMemo(() => {
    return filteredInterventi.reduce((acc, i) => {
      const dataRiferimento =
        tipo === "chiusi"
          ? (i?.dataEsecuzione || i?.data)
          : i?.data;
      const d = dataRiferimento ? new Date(dataRiferimento) : null;
      const key = d && !isNaN(d) ? d.toISOString().slice(0, 10) : "Senza data";
      const label = d && !isNaN(d) ? d.toLocaleDateString("it-IT") : "Senza data";
      if (!acc[key]) acc[key] = { label, items: [] };
      acc[key].items.push(i);
      return acc;
    }, {});
  }, [filteredInterventi, tipo]);

  const dateOrdinate = useMemo(() => {
    return Object.keys(interventiPerData).sort((a, b) =>
      a === "Senza data" ? 1 :
      b === "Senza data" ? -1 :
      new Date(b) - new Date(a)
    );
  }, [interventiPerData]);

  const handleFatturatoChange = (interventoId) => {
    setFatturati((prev) => ({
      ...prev,
      [interventoId]: !prev[interventoId],
    }));
  };

  return (
    <div className="tabella-interventi-container" style={{ padding: "30px" }}>
      <h3 className="interventi-title">
        {tipo === "aperti" ? "Interventi Aperti" : "Interventi Chiusi"}
      </h3>

      {/* SEARCH: sempre visibile (usa parent se c'è, altrimenti locale) */}
      <div className="search-container">
        <div className="search-input-wrapper" style={{ position: "relative", maxWidth: 520 }}>
          <FaSearch className="search-icon" style={{ position: "absolute", left: 10, top: 10, opacity: .55 }} />
          <input
            type="text"
            placeholder="Cerca (cliente, indirizzo, causale, lavori, tecnico, tel, email, n° FI)…"
            value={effectiveQuery}
            onChange={(e) => {
              const val = e.target.value;
              if (typeof onSearchChange === "function") onSearchChange(val);
              else setLocalQuery(val);
            }}
            className="search-input"
            style={{
              width: "100%",
              padding: "10px 12px 10px 34px",
              border: "1px solid #d5d7db",
              borderRadius: 10,
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>
      </div>

      {tipo === "aperti" && (
        <button className="add-intervento-button" onClick={onAggiungi}>
          <FaPlus /> Aggiungi Intervento
        </button>
      )}

      {filteredInterventi.length === 0 ? (
        <p className="text-gray-500 italic">
          Nessun intervento {tipo === "aperti" ? "aperto" : "chiuso"} disponibile
        </p>
      ) : (
        <div className="schede-interventi-wrapper">
          {dateOrdinate.map((key) => (
            <div key={key} className="gruppo-data">
              <h4 className="date-header">{interventiPerData[key].label}</h4>
              {interventiPerData[key].items.map((intervento) => (
                <div key={intervento.id} className="intervento-card">
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: "bold",
                      color: "var(--verde-scuro)",
                    }}
                  >
                    {intervento.cliente ?? "Senza cliente"}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "14px",
                    }}
                  >
                    <div className="campo">
                      <strong>{tipo === "chiusi" ? "Data esecuzione:" : "Data:"}</strong>{" "}
                      {(() => {
                        const dataRiferimento =
                          tipo === "chiusi"
                            ? (intervento.dataEsecuzione || intervento.data)
                            : intervento.data;
                      
                        return dataRiferimento
                          ? new Date(dataRiferimento).toLocaleDateString("it-IT")
                          : "-";
                      })()}
                    </div>

                    <div className="campo campo-expandable">
                      <strong>Indirizzo:</strong>{" "}
                      {(intervento.indirizzo && typeof intervento.indirizzo === "string") ? (
                        expandedFields[`${intervento.id}-indirizzo`] || intervento.indirizzo.length <= 50 ? (
                          intervento.indirizzo
                        ) : (
                          <>
                            {intervento.indirizzo.slice(0, 50)}...
                            <button
                              className="expand-btn"
                              onClick={() => toggleExpand(intervento.id, "indirizzo")}
                            >
                              {expandedFields[`${intervento.id}-indirizzo`] ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </>
                        )
                      ) : (
                        "-"
                      )}
                    </div>

                    <div className="campo campo-expandable">
                      <strong>Causale:</strong>{" "}
                      {(intervento.causale && typeof intervento.causale === "string") ? (
                        expandedFields[`${intervento.id}-causale`] || intervento.causale.length <= 50 ? (
                          intervento.causale
                        ) : (
                          <>
                            {intervento.causale.slice(0, 50)}...
                            <button
                              className="expand-btn"
                              onClick={() => toggleExpand(intervento.id, "causale")}
                            >
                              {expandedFields[`${intervento.id}-causale`] ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </>
                        )
                      ) : (
                        "-"
                      )}
                    </div>

                    <div className="campo">
                      <strong>Ora Inizio:</strong>{" "}
                      {intervento.oraInizio ?? intervento.ora_inizio ?? "-"}
                    </div>

                    <div className="campo">
                      <strong>Ora Fine:</strong>{" "}
                      {intervento.oraFine ?? intervento.ora_fine ?? "-"}
                    </div>

                    <div className="campo">
                      <strong>Pagato:</strong>{" "}
                      {intervento.pagato ? "Sì" : "No"}
                    </div>

                    <div className="campo">
                      <strong>Tecnico:</strong>{" "}
                      {intervento.tecnico ?? "-"}
                    </div>

                    <div className="campo">
                      <strong>N° FI:</strong>{" "}
                      {intervento.numero_foglio_intervento ?? intervento.numeroFoglioIntervento ?? "-"}
                    </div>

                    <div className="campo status" data-stato={intervento.stato}>
                      <strong>Stato:</strong>
                      <span
                        className="status-indicator"
                        style={{
                          display: "inline-block",
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          margin: "0 6px",
                          backgroundColor: getColorByStato(intervento.stato),
                        }}
                      />
                      {intervento.stato ?? "In corso"}
                    </div>
                  </div>

                  <div className="campo lavori-eseguiti">
                    <strong>Lavori Eseguiti:</strong>{" "}
                    {intervento.lavoriEseguiti ?? intervento.lavori_eseguiti ?? "-"}
                  </div>

                  {/* Checkbox FATTURATO (solo visual, solo chiusi) */}
                  {tipo === "chiusi" && (
                    <div className="campo fatturato-field">
                      <input
                        type="checkbox"
                        checked={!!fatturati[intervento.id]}
                        onChange={() => handleFatturatoChange(intervento.id)}
                      />
                      <label>Fatturato</label>
                      {fatturati[intervento.id] && (
                        <div className="stato-flag">
                          <span className="flag-fatturato">FATTURATO!</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="azioni">
                    <button
                      type="button"
                      className={`intervento-flotta-btn ${
                        Number(flottaInterventoId) === Number(intervento.id)
                          ? "active"
                          : ""
                      }`}
                      onClick={() => onImpostaFlotta?.(intervento)}
                      disabled={
                        !intervento.indirizzo ||
                        !String(intervento.indirizzo).trim()
                      }
                      title={
                        intervento.indirizzo
                          ? "Imposta come destinazione del Fiorino"
                          : "Inserire prima un indirizzo"
                      }
                    >
                      <FaTruck />
                      {Number(flottaInterventoId) === Number(intervento.id)
                        ? " Rimuovi dalla Flotta"
                          : " Imposta in Flotta"}
                    </button>

                    <button onClick={() => onPDF(intervento.id)} className="view-btn">
                      <FaFilePdf style={{ marginRight: "6px" }} /> PDF
                    </button>
                    <button onClick={() => onModifica("edit", intervento)} className="edit-btn">
                      Modifica
                    </button>
                    <button onClick={() => onCancella(intervento.id)} className="delete-btn">
                      Cancella
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TabellaInterventi;
